import { Injector } from '@angular/core';
import { fabric } from 'fabric';
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, throttle } from 'lodash';
import { ItemModel } from '../../../../models/itemModel';
import { FLAGS } from '../../../../services/flags.service';
import { ATTR_FIELD_NAME, CUSTOM_FABRIC_DATA } from '../../wb-canvas/fabric-utils';
import { SpaceRepository } from '../../../../state/space.repository';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import {
  TextStyleDeclaration,
  updateMixedFontSizesState,
} from '../../wb-canvas/textbox/textbox-styles';
import { FabricObjectSettings } from '../../context-menus/fabric-object-settings';
import { BoardItemComponentClass, FabricControls } from './board.item';
import { StickyNoteConfigurations } from './board-sticky-note-configurations';
import { CreationContext } from './board-item-configurations';

export class OwnerIdToNameWbUtil {
  private static idToName = new Map<string, string>();

  static getNameIfExists(ownerId: string) {
    return OwnerIdToNameWbUtil.idToName.get(ownerId);
  }

  static cacheOwnerName(ownerId: string, ownerName: string) {
    OwnerIdToNameWbUtil.idToName.set(ownerId, ownerName);
  }

  static disposeCache() {
    OwnerIdToNameWbUtil.idToName.clear();
  }
}
export class BoardStickyNote extends BoardItemComponentClass<StickyNoteConfigurations>(
  fabric.Rect,
) {
  private textBox?: fabric.Textbox;
  private ownerNameTextBox?: fabric.Textbox;
  readonly AngularComponentClass = undefined;

  // Text box config
  private readonly DEFAULT_TEXT_PADDING = 10;
  private readonly DEFAULT_TEXT_MARGIN = 15;
  private readonly DEFAULT_TEXT_WIDTH =
    StickyNoteConfigurations.DEFAULT_WIDTH - this.DEFAULT_TEXT_PADDING * 2;
  private readonly DEFAULT_TEXT_HEIGHT =
    StickyNoteConfigurations.DEFAULT_HEIGHT - this.DEFAULT_TEXT_PADDING * 2;
  private readonly TEXT_COLOR = 'black';
  private readonly DEFAULT_FONT_FAMILY =
    StickyNoteConfigurations.defaultStickyNoteTextSettings().fontFamily;
  private readonly SIGNATURE_FONT_SIZE = 12;
  private readonly SHADOW = '0px 4px 4px rgba(0, 0, 0, 0.25)';
  private readonly MAX_FONT_SIZE_FOR_TYPING_PROMPT = 36;

  static readonly stickyNoteColors = {
    paleYellow: '#FCEB9F',
    limeGreen: '#AEE8B9',
    skyBlue: '#99DAF2',
    lightPink: '#FFB8D2',
    lavender: '#B8B4F5',
  };

  private OWNER_NAME_TEXT_HEIGHT = 20;

  // Services
  private spaceRepo: SpaceRepository;
  private translateService: TranslateService;

  // Subscriptions
  private ownerNameSubscription?: Subscription;
  private selectionSubscription?: Subscription;

  private readonly _stickyNoteInitialized = new BehaviorSubject(false);
  public readonly stickyNoteInitialized = this._stickyNoteInitialized.asObservable();

  private throttledTextChanged?: Function;
  private boardItemsInteractionsManagerService: BoardItemsInteractionsManager;

  constructor(
    injector: Injector,
    configurations: StickyNoteConfigurations,
    creationContext: CreationContext,
  ) {
    super(
      injector,
      ItemModel.StickyNote,
      configurations,
      Object.values(FabricControls),
      creationContext,
    );

    this.set({
      fill: this.configurations.backgroundColor,
      opacity: 1,
      shadow: this.SHADOW,
      stroke: '#F0F4FF',
      strokeWidth: 1,
    } as Partial<this>);

    this.spaceRepo = injector.get<SpaceRepository>(SpaceRepository);
    this.translateService = injector.get<TranslateService>(TranslateService);
    this.boardItemsInteractionsManagerService = injector.get<BoardItemsInteractionsManager>(
      BoardItemsInteractionsManager,
    );
  }

  protected override onFabricAdded(): void {
    super.onFabricAdded();

    this.initThrottledTextChanged();
    this.createAndAddTextMainBox();
    this.updateTextBoxesPosition();
    this.setUpOwnerNameSubscription();
    this.setupTextBoxEventHandlers();
    this.setupSelectionSubscription();

    this._stickyNoteInitialized.next(true);
  }

  protected override onFabricRemoved(): void {
    super.onFabricRemoved();

    this.textBox?.off();
    this.textBox && this.canvas?.remove(this.textBox);
    this.textBox = undefined;

    this.ownerNameTextBox?.off();
    this.ownerNameTextBox && this.canvas?.remove(this.ownerNameTextBox);
    this.ownerNameTextBox = undefined;

    this.ownerNameSubscription?.unsubscribe();
    this.selectionSubscription?.unsubscribe();
  }

  private createAndAddTextMainBox() {
    this.textBox = new fabric.Textbox(this.getStickyNoteText(), {
      width: this.DEFAULT_TEXT_WIDTH,
      height: this.DEFAULT_TEXT_HEIGHT,
      fontFamily: this.configurations.textSettings.fontFamily,
      fontSize: this.configurations.textSettings.fontSize,
      fontWeight: this.configurations.textSettings.fontWeight,
      fontStyle: this.configurations.textSettings.fontStyle,
      underline: this.configurations.textSettings.underline,
      styles: this.configurations.textStyles,
      fill: this.TEXT_COLOR,
      padding: this.DEFAULT_TEXT_PADDING,
      // Should not be processed as a separate object, handling should be done by the creating object
      excludeFromExport: true,
      textAlign: 'left',
      // No events until the user enters edit mode
      evented: false,
      hasControls: false,
      hasBorders: false,
    });

    (this.textBox as any).parentBoardItem = this;
    updateMixedFontSizesState(this.textBox);
    this.textBox.setCoords();
    this.addChildToCanvas(this.textBox);
  }

  private setUpOwnerNameSubscription() {
    const ownerName = OwnerIdToNameWbUtil.getNameIfExists(this.configurations.ownerId);
    if (ownerName) {
      this.initOwnerNameTextBox(ownerName);
      return;
    }
    this.ownerNameSubscription = this.spaceRepo.activeSpacePopulatedUsers$.subscribe((users) => {
      for (const user of users) {
        OwnerIdToNameWbUtil.cacheOwnerName(user._id, user.name);

        // Needed here because the observable might emit multiple values at the same time, making this code execute multiple times, adding multiple owner name text boxes.
        if (this.ownerNameTextBox) {
          this.ownerNameSubscription?.unsubscribe();
          break;
        }

        if (user._id === this.configurations.ownerId) {
          this.initOwnerNameTextBox(user.name);
          this.ownerNameSubscription?.unsubscribe();
          break;
        }
      }
    });
  }

  private initOwnerNameTextBox(ownerName: string) {
    this.addOwnerNameTextBox(ownerName);
    this.updateOwnerNameTextBoxPosition();
    this.updateStickyNoteSizeFromText();
  }

  private addOwnerNameTextBox(ownerName: string) {
    const truncatedOwnerName = this.getTruncatedOwnerName(ownerName);
    this.ownerNameTextBox = new fabric.Textbox(truncatedOwnerName, {
      width: this.DEFAULT_TEXT_WIDTH,
      height: this.OWNER_NAME_TEXT_HEIGHT,
      fontFamily: this.DEFAULT_FONT_FAMILY,
      fontSize: this.SIGNATURE_FONT_SIZE,
      fill: this.TEXT_COLOR,
      padding: this.DEFAULT_TEXT_PADDING,
      // Should not be processed as a separate object, handling should be done by the creating object
      excludeFromExport: true,
      textAlign: 'left',
      // No events until the user enters edit mode
      evented: false,
      hasControls: false,
      hasBorders: false,

      opacity: 0.5,
    });

    this.ownerNameTextBox.setCoords();
    this.addChildToCanvas(this.ownerNameTextBox);
  }

  addChildToCanvas(child: fabric.Object) {
    if (!this.canvas) {
      return;
    }
    // Making sure that they are inserted right above the parent, so that they are stacked correctly
    // This is for the case when several instances of BoardStickyNote are added at the same time
    const currentIndex = this.canvas.getObjects().findIndex((o: any) => o.uid === this.uid);
    this.canvas.insertAt(child, currentIndex + 1, false);
  }

  private setupTextBoxEventHandlers() {
    if (!this.textBox) {
      return;
    }

    this.textBox.on('editing:exited', () => {
      if (!this.textBox) {
        return;
      }

      this.onExitTextEditMode();

      this.syncTextStateWithServerIfNeeded();

      // Adding the prompt to start typing
      if (!this.textBox.text) {
        this.textBox.text = this.getTypingPrompt();
      }
    });

    this.textBox.on('changed', () => this.handleObjectChanged());
    this.textBox.on('fontChanged', () => this.handleObjectChanged());
  }

  private handleObjectChanged() {
    if (this.textBox && !this.textBox.isEditing && this.isTextPlaceholder(this.textBox.text)) {
      this.textBox.text = this.getTypingPrompt();
    }
    this.textBox?.initDimensions();
    this.updateOwnerNameTextBoxPosition();
    this.updateStickyNoteSizeFromText();
    // if the textbox was pasted, we need to reset fill and textBackgroundColor styles
    // to avoid setting these styles
    if (this.textBox && (this.textBox as any).lastInputWasPaste) {
      this.textBox.set({ fill: this.TEXT_COLOR });
      this.textBox.set({ textBackgroundColor: 'transparent' });
      this.textBox.removeStyle('fill');
      this.textBox.removeStyle('textBackgroundColor');
      // update configurations from textbox on paste
      this.updateConfigurationsFromTextbox();
    }

    this.throttledTextChanged?.();
  }

  /**
   * Listen to the active selection object to set itemAlreadySelected if the sticky is selected without direct click (event)
   * for example:
   * - when user duplicates a sticky, the duplicated object is selected automatically
   * - using pointer tool, when user clicks outside the sticky then drag to include the sticky only in the selection
   */
  private setupSelectionSubscription() {
    this.selectionSubscription = this.boardItemsInteractionsManagerService.selection$
      .pipe(filter((selection) => selection?.object === this))
      .subscribe((selection) => {
        if (!selection?.event.e) {
          this.itemAlreadySelected = true;
        }
      });

    this.canvas?.on('mouse:down:before', () => {
      if (this.canvas?.getActiveObject() === this) {
        this.itemAlreadySelected = true;
      }
    });
  }

  initThrottledTextChanged() {
    const DEFAULT_BUFFERING_TIME = 800;

    const bufferingTime =
      (this.flagsService.featureFlagsVariables.event_buffering
        .textbox_change_realtime_update_ms as number) ?? DEFAULT_BUFFERING_TIME;

    this.throttledTextChanged = throttle(
      this.syncTextStateWithServerIfNeeded.bind(this),
      bufferingTime,
    );
  }

  private syncTextStateWithServerIfNeeded() {
    if (!this.textBox || this.configurations.text === this.textBox.text) {
      // Text didn't change
      return;
    }

    const finishedTyping = !this.textBox.isEditing;
    const realTimeTextEnabled = this.flagsService.isFlagEnabled(FLAGS.SPACE_REALTIME_TEXT_UPDATE);

    // The user finished typing or we need to sync while the user is typing if real-time text is enabled
    if (finishedTyping || realTimeTextEnabled) {
      this.configurations.text = this.textBox.text as string;
      this.syncChangesWithServer();
    }
  }

  protected override onMoving(): void {
    this.updateTextBoxesPosition();
  }

  protected override onDoubleClick(): void {
    this.enterEditTextMode();
  }

  // tracking the states of (mouse down and mouse move while down) to enter edit mode
  // when the user clicks on a selected sticky without moving it
  lastEventWasMouseDown = false;
  hasMouseMovedWhileDown = false;
  itemAlreadySelected = false;

  protected override onMouseDown(e) {
    super.onMouseDown(e);
    this.lastEventWasMouseDown = true;
  }

  protected onMouseUp(e) {
    super.onMouseUp(e);
    if (this.itemAlreadySelected && !this.textBox?.isEditing && !this.hasMouseMovedWhileDown) {
      this.enterEditTextMode();
    }
    this.lastEventWasMouseDown = false;
    this.hasMouseMovedWhileDown = false;
  }

  protected onMouseMove(e) {
    super.onMouseMove(e);
    if (this.lastEventWasMouseDown) {
      this.hasMouseMovedWhileDown = true;
    }
  }

  protected override onDeselection(): void {
    this.itemAlreadySelected = false;
  }

  public enterEditTextMode() {
    if (this.configurations.locked || !this.sharedDataService.canModifySession()) {
      return;
    }

    if (!this.textBox) {
      return;
    }

    this.onEnterTextEditMode();

    this.canvas?.setActiveObject(this.textBox);

    // Removing the prompt (text placeholder) to start typing
    if (this.isTextPlaceholder(this.textBox.text)) {
      this.textBox.set({
        text: '',
      });
      this.configurations.text = '';
      this.configurations.textStyles = {};
    }

    // the following code will put the cursor at the end of the text
    this.textBox.setSelectionStart(this.textBox.text?.length ?? 0);
    this.textBox.setSelectionEnd(this.textBox.text?.length ?? 0);

    this.canvas?.requestRenderAll();

    // Start editing
    this.textBox.enterEditing();
    this.textBox.hiddenTextarea?.blur();
    this.textBox.hiddenTextarea?.focus();
  }

  onExitTextEditMode() {
    if (!this.textBox) {
      return;
    }
    if (this.canvas?.getActiveObject() === this.textBox) {
      this.canvas.setActiveObject(this);
    }

    // Disable text interaction after editing
    this.textBox.evented = false;
    // Should not be locked as the lock was only applied to prevent the movement while in edit mode
    this.textBox.lockMovementX = false;
    this.textBox.lockMovementY = false;
    // the onDeselection event is not triggered when the user clicks outside the sticky note when in edit mode
    // because the textbox was the selected object not the sticky note
    // so we need to set itemAlreadySelected to false here also
    this.itemAlreadySelected = false;
  }

  onEnterTextEditMode() {
    if (!this.textBox) {
      return;
    }
    // Enable text interaction for editing
    this.textBox.evented = true;
    // To prevent a bug that allowed user to move the text while in edit mode
    this.textBox.lockMovementX = true;
    this.textBox.lockMovementY = true;
  }

  private updateTextBoxesPosition() {
    let absolutePosition = new fabric.Point(this.left ?? 0, this.top ?? 0);

    if (this.group && !this.textBox?.group) {
      // When the sticky is selected the text box might not be selected (especially if it was selected programatically),
      // Here we need to apply the transformation of the sticky note again to get the correct position of the text box
      const matrix = this.group.calcTransformMatrix();
      absolutePosition = fabric.util.transformPoint(absolutePosition, matrix);
    }

    this.textBox?.set({
      left: absolutePosition.x + this.DEFAULT_TEXT_PADDING,
      top: absolutePosition.y + this.DEFAULT_TEXT_PADDING,
    });

    this.updateOwnerNameTextBoxPosition();

    this.textBox?.setCoords();
  }

  private updateOwnerNameTextBoxPosition() {
    if (!this.textBox || !this.ownerNameTextBox) {
      return;
    }
    const mainTextBoxHeight = this.getMainTextBoxHeight();

    this.ownerNameTextBox.set({
      top: (this.textBox.top ?? 0) + mainTextBoxHeight + this.DEFAULT_TEXT_PADDING,
      left: this.textBox.left ?? 0,
    });
    this.ownerNameTextBox.setCoords();
  }

  private updateStickyNoteSizeFromText() {
    const mainTextBoxHeight = this.getMainTextBoxHeight();
    const ownerNameTextBoxHeight = this.getOwnerNameTextBoxHeight();

    (this as fabric.Object).set({
      height: mainTextBoxHeight + ownerNameTextBoxHeight,
    });
    this.setCoords();
  }

  private getOwnerNameTextBoxHeight() {
    return this.OWNER_NAME_TEXT_HEIGHT + 2 * this.DEFAULT_TEXT_PADDING;
  }

  private getMainTextBoxHeight() {
    const defaultTextBoxHeight =
      StickyNoteConfigurations.DEFAULT_HEIGHT - this.getOwnerNameTextBoxHeight();

    if (!this.textBox) {
      return defaultTextBoxHeight;
    }
    const calculatedHeight = this.textBox.calcTextHeight();
    const newHeight = calculatedHeight + this.DEFAULT_TEXT_MARGIN;
    return Math.max(newHeight, defaultTextBoxHeight);
  }

  override closeLock() {
    super.closeLock();
    this.textBox?.isEditing && this.textBox?.exitEditing();
  }

  override setStateFromConfigurations(configurations: StickyNoteConfigurations) {
    super.setStateFromConfigurations(configurations);

    this.textBox?.set({
      text: this.getStickyNoteText(),
      fontFamily: this.configurations.textSettings.fontFamily,
      fontSize: this.configurations.textSettings.fontSize,
      fontWeight: this.configurations.textSettings.fontWeight,
      fontStyle: this.configurations.textSettings.fontStyle,
      underline: this.configurations.textSettings.underline,
      styles: { ...this.configurations.textStyles },
    });

    this.set({ fill: this.configurations.backgroundColor } as Partial<this>);

    this.canvas?.requestRenderAll();

    this.updateTextBoxesPosition();
    this.updateStickyNoteSizeFromText();
  }

  override toObject(propertiesToInclude?: string[]): any {
    const object = new fabric.Textbox(this.configurations.text).toObject();
    object[CUSTOM_FABRIC_DATA] = {
      top: this.top,
      left: this.left,
      width: this.width,
      height: this.height,
      text: this.configurations.text,
      locked: this.configurations.locked,
      backgroundColor: this.configurations.backgroundColor,
      textSettings: cloneDeep(this.configurations.textSettings),
      textStyles: cloneDeep(this.configurations.textStyles),
    };
    object[ATTR_FIELD_NAME] = 'sticky-note';
    object['text'] = this.configurations.text;

    object['vuid'] = this.vuid;
    object['uid'] = this.uid;
    object['userId'] = this.configurations.ownerId;
    object['order'] = this.configurations.order;

    return object;
  }

  protected openLock() {
    this.lockMovementX = false;
    this.lockMovementY = false;
    this.lockRotation = true;
    this.lockScalingX = true;
    this.lockScalingY = true;
    this.lockUniScaling = true;
    this.lockSkewingX = true;
    this.lockSkewingY = true;
    this.lockScalingFlip = true;
  }

  sendToBack() {
    if (this.canvas?.getObjects()[0] === this) {
      return this;
    }
    // Making sure text box is always in the front of the parent object
    this.textBox?.sendToBack();
    this.ownerNameTextBox?.sendToBack();
    return super.sendToBack();
  }

  bringToFront() {
    const objects = this.canvas?.getObjects();
    if (objects && objects[objects.length - 1] === this) {
      return this;
    }
    // Making sure text box is always in the front of the parent object
    const result = super.bringToFront();
    this.textBox?.bringToFront();
    this.ownerNameTextBox?.bringToFront();
    return result;
  }

  onMoveToIndex(index: number) {
    this.textBox && this.canvas?.moveTo(this.textBox, index + 1);
    this.ownerNameTextBox && this.canvas?.moveTo(this.ownerNameTextBox, index + 2);
  }

  changeBackgroundColor(color: string) {
    this.configurations.backgroundColor = color;

    this.set({ fill: color } as Partial<this>);
    this.canvas?.requestRenderAll();

    this.syncChangesWithServer();
  }

  private isTextPlaceholder(text: string | undefined) {
    if (!text) {
      return false;
    }
    return text === ' ' || text === 'Start typing...';
  }

  private getTypingPrompt() {
    // if the font size is too big, we don't want to show the prompt to not increase the height
    if (this.configurations.textSettings.fontSize > this.MAX_FONT_SIZE_FOR_TYPING_PROMPT) {
      return ' ';
    }
    return `${this.translateService.instant('Start typing')}...`;
  }

  private getStickyNoteText() {
    return this.configurations.text || this.getTypingPrompt();
  }

  private getTruncatedOwnerName(ownerName: string) {
    const containsCJK = /[\u4E00-\u9FFF\u3400-\u4DBF\u3040-\u309F\u30A0-\u30FF\uAC00-\uD7AF]/.test(
      ownerName,
    );

    let truncationLimit = 20;
    if (containsCJK) {
      // because cjk chars take much more space
      // it would be better to measure the text and get the right limit, but this is an efficient good-enough alternative
      truncationLimit = 12;
    }

    return ownerName.length <= truncationLimit
      ? ownerName
      : `${ownerName.substring(0, truncationLimit)}... `;
  }

  isStoredAsFrameItem(): boolean {
    return false;
  }

  public getTextbox(): fabric.Textbox {
    return this.textBox as fabric.Textbox;
  }

  public handleStickyNoteStyleUpdate(property: string, value: any): void {
    if (
      !this.textBox?.isEditing ||
      (this.textBox?.selectionStart === this.textBox?.selectionEnd && this.textBox?.text === '') ||
      (this.textBox?.selectionStart === 0 &&
        this.textBox?.selectionEnd === this.textBox?.text?.length)
    ) {
      // set global style properties if not in editng mode, if textbox is empty, or if the user selected all the text
      this.setTextSetting(property, value);
    }
    this.setCustomTextStyles();
  }

  private updateConfigurationsFromTextbox(): void {
    Object.entries(this.configurations.textSettings).forEach(([key]) => {
      this.setTextSetting(key, this.textBox?.[key as keyof fabric.Textbox]);
    });
    this.setCustomTextStyles();
  }

  private setTextSetting(property: string, value: any): void {
    this.configurations.textSettings[property] = value;
  }

  public setTextSettings(
    style: TextStyleDeclaration,
    defaultTextSettings: FabricObjectSettings,
  ): void {
    this.setTextSetting('fontSize', style['fontSize'] ?? defaultTextSettings.fontSize);
    this.setTextSetting('fontFamily', style['fontFamily'] ?? defaultTextSettings.fontFamily);
    this.setTextSetting('underline', style['underline'] ?? defaultTextSettings.underline);
    this.setTextSetting('fontStyle', style['fontStyle'] ?? defaultTextSettings.fontStyle);
    this.setTextSetting('fontWeight', style['fontWeight'] ?? defaultTextSettings.fontWeight);
  }

  public setCustomTextStyles(): void {
    this.configurations.textStyles = { ...this.textBox?.styles };
  }
}
